import React, { FC, useCallback, useState } from 'react';
import { MaterialReactTable } from 'tanstack-tailwind-table';
import ExpandRowDetails from './ExpandRowDetails';
import { PropsFromScreenManager, TableData } from '../../types';
import { RootState } from '../../reducers/RootReducer';
import {
  compareDataSuccess,
  recentDataSuccess,
  removeCompareData,
  setExpandedRow,
  setPagination,
  toggleScrollTop
} from '../../action/TableAction';
import { setBackToSelectDiamondPage, setDiamondDetailsSuccess, setInitialDiamondOption } from '../../action/DiamondAction';
import { connect, ConnectedProps } from 'react-redux';
import UseTable from './useTable';
import { MRT_SortingState } from '../Tanstack-Table/lib/src/types';
import Button from '../Button/Button';
import DialogBox from '../DialogBox';
import { HKCustomize, HKRing } from '../Images';
import dialogTitleImg from '../../Utils/images/hk-ring.png';
import classes from './Table.module.css';
import { setFooterToggle, setIsShowFooter } from '../../action/FooterAction';
import { setDiamondTabIconTrue } from '../../action/HeaderAction';
import {
  setExclusionOption,
  setJourneyStartedFrom,
  setSelectedDiamondData,
  setSelectedDiamondFail,
  setSelectedDiamondLoading,
} from '../../action/RingAction';
import { setIsCartLoading } from '../../action/CartAction';
import { getDiamondCode } from '../../Utils/getDiamondCode';
import { findNearestValue } from '../../Utils/findNearestCaratValue';
import { getCenterDiamondSize } from '../../Utils/getExclusionDiamondSize';
import { exclusionMatrixData, getExclusionOptions } from '../../Utils/getExclusionOptions';
import { initialExclusionKey } from '../../Utils/initialExclusionKey';
import { DiamondShapeContent } from '../../Utils/commonData';
import { getBuildSearchParams } from '../../Utils/general';

const mapStateToProps = (state: RootState) => ({
  table: state.table,
  filter: state.filter,
  ring: state.ring,
  footer: state.footer,
});

const mapDispatchToProps = {
  setExpandedRow,
  setPagination,
  removeCompareData,
  compareDataSuccess,
  toggleScrollTop,
  setBackToSelectDiamondPage,
  recentDataSuccess,
  setDiamondDetailsSuccess,
  setInitialDiamondOption,
  setFooterToggle,
  setIsShowFooter,
  setDiamondTabIconTrue,
  setJourneyStartedFrom,
  setSelectedDiamondData,
  setSelectedDiamondFail,
  setSelectedDiamondLoading,
  setExclusionOption,
  setIsCartLoading,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export type PropsFromTableRedux = ConnectedProps<typeof connector>;

export interface TableType extends PropsFromTableRedux {
  data: TableData[];
  isLoading: boolean;
  error: unknown | string;
  isFetching?: boolean;
  rowCount?: number;
  enablePagination?: boolean;
  manualSorting?: boolean;
  sorting?: MRT_SortingState;
  setSorting?: any;
  isGrouping?: boolean;
  enableSorting?: boolean;
}

const dialogDetails = [
  {
    key: "DYO",
    title: `Design Your Own Setting`,
    img: HKCustomize,
    paragraph: `Design your own engagement ring by customizing your ring’s style, setting, metal and so much more.`,
  },
  {
    key: "SETTING",
    title: `Start With an Existing Setting`,
    img: HKRing,
    paragraph: `Select a ring setting from our exclusive curation.`,
  },
];

const Table: FC<TableType & PropsFromScreenManager> = (props) => {
  const {
    data,
    isLoading,
    error,
    setExpandedRow,
    table,
    instanceData,
    filter,
    setPagination,
    isFetching = false,
    rowCount = 0,
    enablePagination = false,
    manualSorting = false,
    sorting = [],
    setSorting = () => {},
    compareDataSuccess,
    removeCompareData,
    toggleScrollTop,
    setBackToSelectDiamondPage,
    isGrouping = false,
    enableSorting = true,
    ring,
    footer,
    recentDataSuccess,
    setDiamondDetailsSuccess,
    setInitialDiamondOption,
    setFooterToggle,
    setIsShowFooter,
    setDiamondTabIconTrue,
    setJourneyStartedFrom,
    setSelectedDiamondData,
    setSelectedDiamondFail,
    setSelectedDiamondLoading,
    setExclusionOption,
    setIsCartLoading,
  } = props;
  const { screenManager } = instanceData;
  const {
    isMobileView,
    tableData,
    columns,
    compareDataIds,
    scrollTop,
    setScrollTop,
  } = UseTable({ data, table, filter, isGrouping });

  const [ isOpenDialog, setIsOpenDialog ] = useState(false);
  const [ selectDiamondRow, setSelectDiamondRow ] = useState<TableData | null>(null);
  const pendingDiamondId =
    typeof selectDiamondRow?.id === "string" || typeof selectDiamondRow?.id === "number"
      ? selectDiamondRow.id
      : undefined;
  const closeDialogBox = useCallback(() => {
    setIsOpenDialog(false);
  }, [ setIsOpenDialog ]);
  const openDialogBox = useCallback(() => {
    setIsOpenDialog(true);
  }, [ setIsOpenDialog ]);

  const pathChange = (path: string, styleId?: string | null, id?: string) => {
    if ((screenManager as any).changeScreen) {
      (screenManager as any).changeScreen({
        viewName: path,
        styleId: styleId,
        id: id,
        searchParams: getBuildSearchParams(window.location),
      });
    }
  };

  const handleDesignYourOwnEngagement = (tabValue: string, row: TableData) => {
    document.body.classList.remove("sticky_filter");
    toggleScrollTop(true);
    recentDataSuccess(row);
    setInitialDiamondOption();
    setDiamondDetailsSuccess(row);
    setJourneyStartedFrom("diamond");
    if (tabValue !== "Start With an Existing Setting") {
      setDiamondTabIconTrue("dyo");
      pathChange("dyo", "RB001", `${row.id}`);
    } else {
      setDiamondTabIconTrue("setting");
      pathChange("setting", null, `${row.id}`);
    }
    setIsShowFooter(true);
  };

  const handleSelectDiamond = useCallback(async (row: TableData) => {
    setSelectDiamondRow(row);
    setJourneyStartedFrom("diamond");
    setDiamondDetailsSuccess(row);
    if (Object.keys(ring.options).length > 0) {
      setFooterToggle();
      setIsCartLoading(true);
      const exclusionMatrix = await exclusionMatrixData(footer.styleID);
      const { shape, carat, source } = row;
      let isExclusionDiamond = false;
      if (shape && carat && source) {
        isExclusionDiamond = ring.exclusionOption.includes(
          findNearestValue(+carat)
        );
        const result = await getDiamondCode(
          { shape: shape as string, type: source as string },
          setSelectedDiamondLoading,
          setSelectedDiamondFail
        );
        if (result) {
          if (result["Diamond_Shape"] !== ring.options["Diamond_Shape"]) {
            const ringExclusionMatrix = getExclusionOptions(
              exclusionMatrix,
              "Diamond_Shape",
              result["Diamond_Shape"],
              initialExclusionKey(footer.styleID.includes("RP")),
              ring.options
            );
            ringExclusionMatrix["Diamond_Shape"] = ring.exclusionOption.filter(
              (option) =>
                DiamondShapeContent.map((v: { code: any }) => v.code).includes(
                  option
                )
            ) as string[];
            isExclusionDiamond = [
              ...Object.values(ringExclusionMatrix).flat(),
            ]?.includes(findNearestValue(+carat));
            setExclusionOption([ ...Object.values(ringExclusionMatrix).flat() ]);
            setSelectedDiamondData({
              ...result,
              Center_Diamond_Size: isExclusionDiamond
                ? getCenterDiamondSize([
                  ...Object.values(ringExclusionMatrix).flat(),
                ])
                : findNearestValue(+carat),
            });
          } else {
            setSelectedDiamondData({
              ...result,
              Center_Diamond_Size: isExclusionDiamond
                ? getCenterDiamondSize(ring.exclusionOption)
                : findNearestValue(+carat),
            });
          }
        }
      }
      setIsCartLoading(false);
    } else {
      openDialogBox();
    }
    if ((screenManager as any).props.setStateManagerData) {
      (screenManager as any).props.setStateManagerData("selectedDiamond", row);
    }
  }, [ ring, footer, screenManager, setJourneyStartedFrom, setDiamondDetailsSuccess, setFooterToggle, setIsCartLoading, setSelectedDiamondLoading, setSelectedDiamondFail, setExclusionOption, setSelectedDiamondData, openDialogBox ]);

  if (error) {
    return <div className={"p-5 mx-auto"}>Error...</div>;
  }
  return (
    <div className={"mx-auto max-w-site"}>
      <MaterialReactTable
        //@ts-ignore
        columns={columns}
        data={tableData}
        enableTopToolbar={false}
        enableExpandAll={false}
        enableSorting={enableSorting}
        enableColumnActions={false}
        enablePagination={enablePagination}
        enableGrouping={isGrouping}
        manualPagination={enablePagination}
        manualSorting={manualSorting}
        rowCount={rowCount}
        enableTableHead={true} //{!isMobileView}
        displayColumnDefOptions={{ "mrt-row-expand": { size: isMobileView ? 40 : 0 } }}
        initialState={{
          compareDataSuccess,
          ...(isGrouping && { expanded: true }),
          removeCompareData,
          setExpandedRow,
          toggleScrollTop,
          setPagination,
          setBackToSelectDiamondPage,
          setScrollTop,
          scrollTop: scrollTop,
          reduxPagination: table.pagination,
          activeTab: table.activeTab,
          isMobileView: isMobileView,
          isLoading: isLoading || isFetching,
          pagination: {
            pageIndex: table.pagination[table.activeTab].pageIndex,
            pageSize: table.pagination[table.activeTab].pageSize,
          },
          expandRow: table.expandRow,
          pendingDiamondId: isOpenDialog ? pendingDiamondId : undefined,
          compareDataIds: compareDataIds,
          instanceData: instanceData,
          scrollToTop: table.scrollToTop,
          onSelectDiamond: handleSelectDiamond,
          ...(isGrouping && { grouping: ["type"] }),
          paginationArray:
            +rowCount > table.pagination[table.activeTab].pageSize
              ? [ 50, 100, 150, 200 ]
              : [],
        }}
        {...(manualSorting && { onSortingChange: setSorting })}
        state={{
          showProgressBars: isFetching,
          ...(manualSorting && { sorting: sorting }),
        }}
        renderDetailPanel={({ row }) => {
          return +row.id === +row.index ? (
            <ExpandRowDetails
                row={row.original}
                instanceData={instanceData}
                onSelectDiamond={handleSelectDiamond}
            />
          ) : (
            ""
          );
        }}
      />
      <DialogBox
        isOpen={isOpenDialog}
        closeModal={closeDialogBox}
        titleClassName={classes.dialogBoxTitle}
        isIcon={true}
        title={""}
      >
        <div className={classes.dialogSetting}>
          <img
            className={classes.dialog_img}
            src={dialogTitleImg}
            alt={"Settings"}
          />
          <p className={classes.dialog_subtitle}>Our Settings</p>
          <p className={classes.dialog_text}>
            Design your own setting or choose <br/> a pre-crafted setting.
          </p>
          <div className={classes.buttonGroup}>
            {dialogDetails.map((detail, index) => {
              return (
                <Button
                  key={index}
                  buttonType={"btnSecondary"}
                  type={"button"}
                  className={classes.btnSetting}
                  isDisabled={
                    !!selectDiamondRow &&
                    detail.title === "Design Your Own Setting" &&
                    (((selectDiamondRow.shape as string)?.toLowerCase() === "marquise" &&
                        selectDiamondRow.carat &&
                        +selectDiamondRow.carat >= 1.75) ||
                      [ "square cushion", "heart" ].includes(
                        (selectDiamondRow.shape as string)?.toLowerCase() as string
                      ))
                  }
                  onClick={() => {
                    if (!selectDiamondRow) return;
                    closeDialogBox();
                    handleDesignYourOwnEngagement(
                      detail.title === "Start With an Existing Setting"
                        ? "Start With an Existing Setting"
                        : "Design Your Own Setting",
                      selectDiamondRow
                    );
                  }}
                >
                  <span>{detail.title}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </DialogBox>
    </div>
  );
};

export default connector(Table);
